import React, { useState } from 'react';
import { Link } from 'react-router-dom';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

/** Pure — validate signup fields before POST. */
export function validateTrialForm({ email, password, name, restaurant_name }) {
  const errors = {};
  if (!email || !email.includes('@')) errors.email = 'Valid email required';
  if (!password || password.length < 10) errors.password = 'Password must be at least 10 characters';
  if (!name || !name.trim()) errors.name = 'Name required';
  if (!restaurant_name || !restaurant_name.trim()) errors.restaurant_name = 'Restaurant name required';
  return { ok: Object.keys(errors).length === 0, errors };
}

export default function TrialSignup() {
  const [form, setForm] = useState({
    email: '',
    password: '',
    name: '',
    restaurant_name: '',
  });
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const onChange = (e) => {
    setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    const v = validateTrialForm(form);
    if (!v.ok) {
      setError(Object.values(v.errors)[0]);
      return;
    }
    setSubmitting(true);
    try {
      const res = await fetch(`${API_URL}/api/trial/signup`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        const detail = err.detail;
        throw new Error(typeof detail === 'string' ? detail : 'Signup failed');
      }
      const data = await res.json();
      localStorage.setItem('token', data.token);
      localStorage.setItem('auth_token', data.token);
      if (data.default_organization?.id) {
        localStorage.setItem('current_organization_id', data.default_organization.id);
      }
      // Full navigation so AuthProvider reloads /api/auth/me with the new token.
      window.location.assign(data.next_path || '/first-run');
    } catch (err) {
      setError(err.message || String(err));
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center px-4" data-testid="trial-signup">
      <div className="w-full max-w-md bg-white border border-slate-200 rounded-xl p-8 shadow-sm">
        <p className="text-sm font-medium text-emerald-700 mb-1">FoodOps</p>
        <h1 className="text-2xl font-semibold text-slate-900 mb-2">Start your free trial</h1>
        <p className="text-sm text-slate-600 mb-6">
          14 days · SMB costing · upload one invoice and see dish GP%
        </p>
        <form onSubmit={onSubmit} className="space-y-4">
          <label className="block text-sm">
            <span className="text-slate-700">Your name</span>
            <input
              name="name"
              value={form.name}
              onChange={onChange}
              className="mt-1 w-full border border-slate-300 rounded-lg px-3 py-2"
              data-testid="trial-name"
              autoComplete="name"
            />
          </label>
          <label className="block text-sm">
            <span className="text-slate-700">Restaurant name</span>
            <input
              name="restaurant_name"
              value={form.restaurant_name}
              onChange={onChange}
              className="mt-1 w-full border border-slate-300 rounded-lg px-3 py-2"
              data-testid="trial-restaurant"
              autoComplete="organization"
            />
          </label>
          <label className="block text-sm">
            <span className="text-slate-700">Work email</span>
            <input
              name="email"
              type="email"
              value={form.email}
              onChange={onChange}
              className="mt-1 w-full border border-slate-300 rounded-lg px-3 py-2"
              data-testid="trial-email"
              autoComplete="email"
            />
          </label>
          <label className="block text-sm">
            <span className="text-slate-700">Password (10+ chars)</span>
            <input
              name="password"
              type="password"
              value={form.password}
              onChange={onChange}
              className="mt-1 w-full border border-slate-300 rounded-lg px-3 py-2"
              data-testid="trial-password"
              autoComplete="new-password"
            />
          </label>
          {error && (
            <p className="text-rose-700 text-sm" role="alert" data-testid="trial-error">
              {error}
            </p>
          )}
          <button
            type="submit"
            disabled={submitting}
            className="w-full bg-emerald-700 text-white rounded-lg py-2.5 font-medium hover:bg-emerald-800 disabled:opacity-60"
            data-testid="trial-submit"
          >
            {submitting ? 'Creating…' : 'Create trial account'}
          </button>
        </form>
        <p className="mt-4 text-sm text-slate-500">
          Already have an account? <Link to="/login" className="text-emerald-700 underline">Sign in</Link>
        </p>
      </div>
    </div>
  );
}
